"use client";

/**
 * Orik's occupants of the harness conversation panel's seats.
 *
 * The harness hands a session-scoped occupant the conversation's `sessionId` (and its seat's owner props);
 * everything else these read is Orik's own state or the session plane's catalogue, so they render wherever the
 * panel does. Registration lives in `harness/orik-seats.ts`.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowUpRightIcon, ChartNoAxesCombinedIcon, Code2Icon, MicIcon, SearchIcon, SquareIcon, XIcon } from "lucide-react";
import { OrikMark } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { GeminiLiveDictationAdapter } from "@/lib/gemini-live-dictation-adapter";
import { useCatalogueSession } from "@/lib/agent/catalogue-store";
import { useTranscription } from "@/lib/transcription";

/** Props every session-scoped occupant receives: the conversation, and whether the composer is locked. */
export interface SessionSeatProps {
  readonly sessionId: string;
  readonly locked?: boolean;
}

/** The draft's insertion face, as the harness hands it to composer occupants. */
interface InputActions {
  captureInsertion(): unknown;
  insertText(text: string, span: unknown): boolean;
}

/** One live dictation, as the adapter runs it. */
type Dictation = ReturnType<GeminiLiveDictationAdapter["listen"]>;

/**
 * Voice input, in the composer's activity seat between the model picker and Send.
 *
 * Speech is transcribed by Gemini Live while the user talks; the text being heard previews in the toolbar,
 * and stopping inserts the final transcript at the cursor as one undoable edit — through the harness's own
 * insertion, which refuses if the draft changed in the meantime rather than overwriting it. While dictation
 * runs, the control takes the toolbar's width (`onActiveChange`). Rendered only where the deployment has
 * transcription configured.
 */
export function OrikDictationControl({ locked = false, onActiveChange, inputActions }: SessionSeatProps & {
  readonly onActiveChange: (active: boolean) => void;
  readonly inputActions?: InputActions;
}) {
  const enabled = useTranscription(state => state.enabled);
  const [heard, setHeard] = useState<string>();
  const [error, setError] = useState<string>();
  const dictation = useRef<Dictation | undefined>(undefined);
  const insertion = useRef<unknown>(undefined);
  // Releasing the toolbar width on unmount is the seat's contract; a dictation still running is cancelled.
  useEffect(() => () => { dictation.current?.cancel(); onActiveChange(false); }, [onActiveChange]);
  if (!enabled || inputActions === undefined) return null;

  const start = (): void => {
    setError(undefined);
    insertion.current = inputActions.captureInsertion();
    const session = new GeminiLiveDictationAdapter().listen();
    dictation.current = session;
    setHeard("");
    onActiveChange(true);
    session.onSpeech(result => setHeard(result.transcript));
    session.onSpeechEnd(result => {
      const text = result.transcript.trim();
      if (text !== "" && !inputActions.insertText(text, insertion.current)) setError("The draft changed while you were speaking, so the transcript was not inserted.");
    });
  };
  const end = async (cancel: boolean): Promise<void> => {
    const session = dictation.current;
    dictation.current = undefined;
    if (cancel) session?.cancel();
    else await session?.stop().catch(() => setError("Voice input stopped unexpectedly."));
    setHeard(undefined);
    onActiveChange(false);
  };

  if (heard === undefined) {
    return <span data-orik-ui="" className="flex items-center gap-1">
      <Button type="button" variant="ghost" size="icon" disabled={locked} onClick={start} aria-label="Start voice input"
        className="size-7 rounded-full text-muted-foreground hover:text-foreground"><MicIcon className="size-4" /></Button>
      {error !== undefined && <span role="alert" className="text-destructive max-w-48 truncate text-xs" title={error}>{error}</span>}
    </span>;
  }
  return <span data-orik-ui="" role="group" aria-label="Voice input" className="flex min-w-0 flex-1 items-center gap-2">
    <span className="size-2 shrink-0 rounded-full bg-destructive motion-safe:animate-pulse" aria-hidden="true" />
    <span role="status" className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{heard === "" ? "Listening…" : heard}</span>
    <Button type="button" variant="ghost" size="icon" onClick={() => { void end(true); }} aria-label="Cancel voice input" className="size-7 rounded-full"><XIcon className="size-4" /></Button>
    <Button type="button" variant="outline" size="icon" onClick={() => { void end(false); }} aria-label="Stop voice input" className="size-7 rounded-full"><SquareIcon className="size-3 fill-current" /></Button>
  </span>;
}

/** Orik's mark, where the blank conversation's hero shows a brand mark beside its headline. */
export function OrikHeroMark({ size = 34, className }: { readonly size?: number; readonly className?: string }) {
  return <OrikMark data-orik-ui="" className={`text-primary ${className ?? ""}`} style={{ width: size, height: size }} />;
}

/** Where a new conversation can start, as prompts the user finishes. */
const STARTERS = [
  { title: "Build something", description: "Turn an idea into working code", prompt: "Help me build ", Icon: Code2Icon },
  { title: "Explore a question", description: "Research, compare, and understand", prompt: "Research and compare ", Icon: SearchIcon },
  { title: "Make sense of data", description: "Find the story in your numbers", prompt: "Help me analyze ", Icon: ChartNoAxesCombinedIcon },
];

/**
 * Starter prompts above a blank conversation's composer.
 *
 * Each puts the beginning of a request in the draft for the user to finish; nothing is sent. Shown only while
 * the conversation is blank — once it has a message, the composer dock belongs to the conversation.
 */
export function OrikStarters({ sessionId, inputActions }: SessionSeatProps & { readonly inputActions?: { setDraft(text: string): void } }) {
  const blank = useCatalogueSession(sessionId)?.blank === true;
  if (!blank || inputActions === undefined) return null;
  return <div data-orik-ui="" data-slot="orik-starters" className="mb-1 grid w-full grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
    {STARTERS.map(({ title, description, prompt, Icon }) => (
      <button key={title} type="button" onClick={() => inputActions.setDraft(prompt)}
        className="group flex items-center gap-3 rounded-xl border border-border bg-card/50 px-4 py-3 text-left transition-colors hover:border-primary/30 hover:bg-card focus-visible:outline-2 focus-visible:outline-ring sm:block sm:py-4">
        <span className="flex items-center justify-between"><Icon className="size-4 text-muted-foreground transition-colors group-hover:text-primary" /><ArrowUpRightIcon className="hidden size-3.5 text-muted-foreground/60 sm:block" /></span>
        <span className="block"><span className="block text-xs font-medium sm:mt-3">{title}</span><span className="mt-1 block text-[11px] leading-4 text-muted-foreground">{description}</span></span>
      </button>
    ))}
  </div>;
}
